// src/pages/admin/AdminAnalytics.js
import React, { useState, useEffect, useRef } from 'react';
import {
  FiTrendingUp,
  FiUsers,
  FiDollarSign,
  FiPercent,
  FiPieChart,
  FiBarChart2,
  FiActivity,
  FiTarget,
  FiDownload
} from 'react-icons/fi';
import API from '../../services/api';
import { toast } from 'react-toastify';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  const [profitCalc, setProfitCalc] = useState({
    revenue: '0',
    cogs: '0',
    commission: '15',
    marketing: '0'
  });

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await API.get('/admin/analytics', { params: { days: dateRange } });
      const data = res?.data;
      if (mountedRef.current) {
        if (data && (data.success || data.data)) {
          setAnalytics(data.data || data);
        } else {
          setAnalytics(null);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des analytics');
      if (mountedRef.current) setAnalytics(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const parseNumber = (v) => {
    const n = parseFloat(String(v).replace(/[, ]+/g, '')) || 0;
    return Number.isFinite(n) ? n : 0;
  };

  const calculateProfit = () => {
    const totalRevenue = parseNumber(profitCalc.revenue);
    const costs = parseNumber(profitCalc.cogs);
    const commissionPct = Math.max(0, Math.min(100, parseNumber(profitCalc.commission)));
    const commissionAmount = (totalRevenue * commissionPct) / 100;
    const marketingCosts = parseNumber(profitCalc.marketing);

    const netProfit = totalRevenue - costs - commissionAmount - marketingCosts;
    const profitMargin =
      totalRevenue > 0 ? Number(((netProfit / totalRevenue) * 100).toFixed(2)) : 0;
    const roi = costs > 0 ? Number(((netProfit / costs) * 100).toFixed(2)) : 0;

    return {
      revenue: totalRevenue,
      costs,
      commissionAmount,
      marketingCosts,
      netProfit,
      profitMargin,
      roi
    };
  };

  const profit = calculateProfit();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const conversionRate = analytics?.conversionRate ?? 0;

  const stats = analytics
    ? {
        thisMonthRevenue: analytics.thisMonthRevenue || 0,
        thisMonthOrders: analytics.thisMonthOrders || 0,
        thisMonthUsers: analytics.thisMonthUsers || 0,
        revenueGrowth: analytics.revenueGrowth || 0,
        orderGrowth: analytics.orderGrowth || 0,
        userGrowth: analytics.userGrowth || 0,
        conversionGrowth: analytics.conversionGrowth || 0
      }
    : {
        thisMonthRevenue: 0,
        thisMonthOrders: 0,
        thisMonthUsers: 0,
        revenueGrowth: 0,
        orderGrowth: 0,
        userGrowth: 0,
        conversionGrowth: 0
      };

  const customerSeg = analytics?.customerSegmentation || {};
  const newCustomers = customerSeg.new || 0;
  const returningCustomers = customerSeg.returning || 0;
  const vipCustomers = customerSeg.vip || 0;

  const totalCustomers = newCustomers + returningCustomers + vipCustomers;
  const newCustomerPct =
    totalCustomers > 0 ? Math.round((newCustomers / totalCustomers) * 100) : 0;
  const returningCustomerPct =
    totalCustomers > 0 ? Math.round((returningCustomers / totalCustomers) * 100) : 0;

  const ordersByStatus = analytics?.ordersByStatus || {};
  const completedOrders = ordersByStatus.completed || 0;
  const pendingOrders = ordersByStatus.pending || 0;
  const cancelledOrders = ordersByStatus.cancelled || 0;
  const totalOrdersStatus = completedOrders + pendingOrders + cancelledOrders;

  const geoDistribution = Array.isArray(analytics?.topRegions) ? analytics.topRegions : [];

  const devices = analytics?.devices || { mobile: 0, desktop: 0, tablet: 0 };

  const satisfactionScore = analytics?.avgRating || 0;
  const satisfactionMax = 5;
  const retentionRate = analytics?.retentionRate || 0;

  const cartAbandonmentRate =
    totalOrdersStatus > 0
      ? Math.round((cancelledOrders / totalOrdersStatus) * 100)
      : 0;

  const redemptionRate =
    totalOrdersStatus > 0
      ? Math.round((completedOrders / totalOrdersStatus) * 100)
      : 0;

  const customerLTV =
    stats.thisMonthUsers > 0
      ? Math.round(stats.thisMonthRevenue / stats.thisMonthUsers)
      : 0;

  const exportReport = () => {
    try {
      const rows = [];

      rows.push(['Section', 'Métrique', 'Valeur']);

      rows.push(['Growth', 'Croissance CA (%)', stats.revenueGrowth]);
      rows.push(['Growth', 'Croissance commandes (%)', stats.orderGrowth]);
      rows.push(['Growth', 'Croissance utilisateurs (%)', stats.userGrowth]);
      rows.push(['Growth', 'Croissance conversion (%)', stats.conversionGrowth]);

      rows.push(['Marketing Analytics', 'Revenue Total (MAD)', stats.thisMonthRevenue]);
      rows.push(['Marketing Analytics', 'Commandes', stats.thisMonthOrders]);
      rows.push(['Marketing Analytics', 'Utilisateurs (mois)', stats.thisMonthUsers]);
      rows.push([
        'Marketing Analytics',
        'Panier Moyen (MAD)',
        analytics?.aov ?? 0
      ]);
      rows.push([
        'Marketing Analytics',
        'Taux de conversion',
        `${conversionRate}%`
      ]);

      rows.push(['Customer Insights', 'Nouveaux clients', newCustomers]);
      rows.push(['Customer Insights', 'Clients réguliers', returningCustomers]);
      rows.push(['Customer Insights', 'VIP', vipCustomers]);
      rows.push(['Customer Insights', '% nouveaux clients', `${newCustomerPct}%`]);
      rows.push([
        'Customer Insights',
        '% clients réguliers',
        `${returningCustomerPct}%`
      ]);
      rows.push(['Customer Insights', 'Taux de retour (90j)', `${retentionRate}%`]);

      rows.push(['Profit', 'Revenue Total (MAD)', profit.revenue]);
      rows.push(['Profit', 'Coûts produits (MAD)', profit.costs]);
      rows.push([
        'Profit',
        `Commission (${parseNumber(profitCalc.commission)}%) (MAD)`,
        profit.commissionAmount
      ]);
      rows.push(['Profit', 'Coûts marketing (MAD)', profit.marketingCosts]);
      rows.push(['Profit', 'Profit net (MAD)', profit.netProfit]);
      rows.push(['Profit', 'Marge bénéficiaire (%)', profit.profitMargin]);
      rows.push(['Profit', 'ROI (%)', profit.roi]);

      geoDistribution.forEach((loc) => {
        rows.push([
          'Répartition Géographique',
          loc.name,
          `${loc.percentage ?? 0}%`
        ]);
      });

      rows.push(['Devices', 'Mobile (%)', devices.mobile ?? 0]);
      rows.push(['Devices', 'Desktop (%)', devices.desktop ?? 0]);
      rows.push(['Devices', 'Tablet (%)', devices.tablet ?? 0]);

      rows.push([
        'Métriques Clés',
        'Taux abandon panier',
        `${cartAbandonmentRate}%`
      ]);
      rows.push([
        'Métriques Clés',
        'Taux rédemption',
        `${redemptionRate}%`
      ]);
      rows.push(['Métriques Clés', 'Customer LTV (MAD)', customerLTV]);
      rows.push([
        'Métriques Clés',
        'Satisfaction',
        `${satisfactionScore}/${satisfactionMax}`
      ]);

      const toCsvLine = (cols) =>
        cols
          .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
          .join(',');

      const csvContent = rows.map(toCsvLine).join('\r\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.download = `maxula-analytics-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Rapport exporté en CSV (voir vos téléchargements)');
    } catch (err) {
      toast.error("Erreur lors de l’export du rapport");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics & Rapports</h1>
            <p className="text-gray-600 mt-1">
              Insights détaillés style Groupon Impact Report
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="7">7 jours</option>
              <option value="30">30 jours</option>
              <option value="90">90 jours</option>
            </select>
            <button
              onClick={exportReport}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              type="button"
            >
              <FiDownload className="mr-2" />
              Exporter
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <FiBarChart2 className="mr-2 text-primary-600" />
          Marketing Analytics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Revenue Total</p>
            <p className="text-2xl font-bold text-gray-900">
              {stats.thisMonthRevenue.toLocaleString()} MAD
            </p>
            <p className="text-xs text-green-600 mt-1">
              Croissance: {stats.revenueGrowth}%
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Commandes</p>
            <p className="text-2xl font-bold text-gray-900">
              {stats.thisMonthOrders}
            </p>
            <p className="text-xs text-green-600 mt-1">
              Croissance: {stats.orderGrowth}%
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Utilisateurs (mois)</p>
            <p className="text-2xl font-bold text-gray-900">
              {stats.thisMonthUsers}
            </p>
            <p className="text-xs text-green-600 mt-1">
              Croissance: {stats.userGrowth}%
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-1">Taux Conversion</p>
            <p className="text-2xl font-bold text-gray-900">
              {conversionRate}%
            </p>
            <p className="text-xs text-green-600 mt-1">
              Croissance: {stats.conversionGrowth}%
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <FiUsers className="mr-2 text-blue-600" />
          Customer Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
            <FiUsers className="text-3xl mb-2 opacity-80" />
            <p className="text-sm opacity-90">Nouveaux Clients</p>
            <p className="text-3xl font-bold my-2">{newCustomers}</p>
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-white border-opacity-20">
              <span className="text-sm">% du total</span>
              <span className="text-lg font-semibold">{newCustomerPct}%</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
            <FiActivity className="text-3xl mb-2 opacity-80" />
            <p className="text-sm opacity-90">Clients Réguliers</p>
            <p className="text-3xl font-bold my-2">{returningCustomers}</p>
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-white border-opacity-20">
              <span className="text-sm">% du total</span>
              <span className="text-lg font-semibold">{returningCustomerPct}%</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
            <FiTrendingUp className="text-3xl mb-2 opacity-80" />
            <p className="text-sm opacity-90">Taux de Retour (90j)</p>
            <p className="text-3xl font-bold my-2">{retentionRate}%</p>
            <div className="mt-4 pt-4 border-t border-white border-opacity-20">
              <span className="text-sm">
                Clients qui reviendront probablement dans 90 jours
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <FiDollarSign className="mr-2 text-green-600" />
          Calculateur de Profit (Groupon Style)
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Entrez vos données</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Revenue Total (MAD)
                </label>
                <input
                  type="number"
                  value={profitCalc.revenue}
                  onChange={(e) =>
                    setProfitCalc({ ...profitCalc, revenue: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="50000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coût des Produits/Services (MAD)
                </label>
                <input
                  type="number"
                  value={profitCalc.cogs}
                  onChange={(e) =>
                    setProfitCalc({ ...profitCalc, cogs: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="20000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commission Maxula (%)
                </label>
                <input
                  type="number"
                  value={profitCalc.commission}
                  onChange={(e) =>
                    setProfitCalc({ ...profitCalc, commission: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="15"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coûts Marketing (MAD)
                </label>
                <input
                  type="number"
                  value={profitCalc.marketing}
                  onChange={(e) =>
                    setProfitCalc({ ...profitCalc, marketing: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow p-6">
            <h3 className="font-semibold mb-4">Résultats</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="text-gray-600">Revenue Total</span>
                <span className="font-semibold text-lg">
                  {profit.revenue.toLocaleString()} MAD
                </span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="text-gray-600">Coût Produits</span>
                <span className="text-red-600">
                  -{profit.costs.toLocaleString()} MAD
                </span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="text-gray-600">
                  Commission ({parseNumber(profitCalc.commission)}%)
                </span>
                <span className="text-red-600">
                  -{profit.commissionAmount.toLocaleString()} MAD
                </span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="text-gray-600">Coûts Marketing</span>
                <span className="text-red-600">
                  -{profit.marketingCosts.toLocaleString()} MAD
                </span>
              </div>
              <div className="flex justify-between items-center py-3 bg-white rounded-lg px-4 mt-4">
                <span className="font-bold text-lg">Profit Net</span>
                <span
                  className={`font-bold text-2xl ${
                    profit.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {profit.netProfit.toLocaleString()} MAD
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-600">Marge Bénéficiaire</p>
                  <p className="text-xl font-bold text-primary-600">
                    {profit.profitMargin}%
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm text-gray-600">ROI</p>
                  <p className="text-xl font-bold text-purple-600">
                    {profit.roi}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4 flex items-center">
            <FiPieChart className="mr-2 text-blue-600" />
            Répartition Géographique
          </h3>
          {geoDistribution.length > 0 ? (
            <div className="space-y-3">
              {geoDistribution.map((loc) => {
                const percent = loc.percentage ?? 0;
                return (
                  <div key={loc.name}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">
                        {loc.name}
                      </span>
                      <span className="text-sm font-semibold">
                        {percent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-blue-500"
                        style={{
                          width: `${Math.max(0, Math.min(100, percent))}%`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Aucune donnée disponible.</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4 flex items-center">
            <FiActivity className="mr-2 text-green-600" />
            Répartition par Device
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Mobile</span>
              <span className="font-semibold">{devices.mobile ?? 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Desktop</span>
              <span className="font-semibold">{devices.desktop ?? 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tablet</span>
              <span className="font-semibold">{devices.tablet ?? 0}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4 flex items-center">
            <FiPercent className="mr-2 text-purple-600" />
            Métriques Clés
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Taux abandon panier</span>
              <span className="font-semibold text-red-600">
                {cartAbandonmentRate}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Taux rédemption</span>
              <span className="font-semibold text-green-600">
                {redemptionRate}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Customer LTV</span>
              <span className="font-semibold">
                {customerLTV} MAD
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Satisfaction</span>
              <span className="font-semibold text-yellow-600">
                {satisfactionScore}/{satisfactionMax} ⭐
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
